import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useLanguage } from '../context/LanguageContext';
import { company } from '../config/company';
import s from '../styles/legalStyles';

const handleOpenPreferences = () => {
  // Clear consent so the banner reappears
  localStorage.removeItem('casaothers_cookie_consent');
  window.dispatchEvent(new Event('cookie-consent-reset'));
};

const CookiesPolicy = () => {
  const { language } = useLanguage();
  const isNL = language === 'nl';

  return (
    <div style={s.page}>
      <SEO
        title={isNL ? 'Cookiebeleid' : 'Cookies Policy'}
        description={isNL
          ? `Cookiebeleid van ${company.brand}. Welke cookies wij gebruiken en hoe u uw voorkeuren beheert.`
          : `Cookies policy of ${company.brand}. Which cookies we use and how to manage your preferences.`}
      />
      <div style={s.container}>
        <h1 style={s.title}>{isNL ? 'Cookiebeleid' : 'Cookies Policy'}</h1>
        <p style={s.lastUpdated}>
          {isNL ? 'Laatst bijgewerkt:' : 'Last updated:'} {company.effectiveDate}
        </p>

        {isNL ? (
          <article style={s.article}>
            <p>
              {company.brand} maakt gebruik van cookies en vergelijkbare technieken om de website goed te laten
              functioneren, het gebruik te analyseren en uw ervaring te verbeteren. In dit cookiebeleid leggen wij uit
              welke cookies wij gebruiken en hoe u deze kunt beheren.
            </p>

            <h2 style={s.h2}>1. Wat zijn cookies?</h2>
            <p>
              Cookies zijn kleine tekstbestanden die op uw apparaat worden geplaatst wanneer u onze website bezoekt.
              Ze helpen ons om bepaalde voorkeuren te onthouden en het gebruik van de site te meten.
            </p>

            <h2 style={s.h2}>2. Welke cookies gebruiken wij?</h2>

            <h3 style={s.h3}>2.1 Functionele cookies (altijd actief)</h3>
            <p>Deze cookies zijn noodzakelijk voor de werking van de website. U kunt deze niet uitschakelen.</p>
            <ul style={s.list}>
              <li><strong>casaothers_lang</strong> — onthoudt uw taalvoorkeur (NL/EN). Bewaartermijn: 1 jaar.</li>
              <li><strong>casaothers_admin_token</strong> — admin login (alleen voor beheerders). Bewaartermijn: 7 dagen.</li>
              <li><strong>casaothers_cookie_consent</strong> — onthoudt uw cookievoorkeuren. Bewaartermijn: 1 jaar.</li>
            </ul>

            <h3 style={s.h3}>2.2 Analytische cookies (toestemming vereist)</h3>
            <p>
              Wij gebruiken <strong>Google Analytics 4</strong> en <strong>Google Tag Manager</strong> om geanonimiseerd
              te begrijpen hoe bezoekers onze site gebruiken. Hiermee verbeteren wij de gebruikerservaring en optimaliseren
              wij ons aanbod.
            </p>
            <ul style={s.list}>
              <li><strong>_ga</strong>, <strong>_ga_*</strong> — Google Analytics 4. Bewaartermijn: 13 maanden.</li>
              <li><strong>_gid</strong> — onderscheidt unieke bezoekers. Bewaartermijn: 24 uur.</li>
              <li><strong>_gat</strong> — beperkt het aantal aanvragen. Bewaartermijn: 1 minuut.</li>
            </ul>
            <p>
              Wij hebben een verwerkersovereenkomst met Google en IP-anonimisering ingeschakeld waar mogelijk.
            </p>

            <h2 style={s.h2}>3. Uw voorkeuren beheren</h2>
            <p>
              U kunt uw cookievoorkeuren op elk moment aanpassen via de onderstaande knop, of door cookies in uw
              browserinstellingen te verwijderen.
            </p>
            <button style={s.button} onClick={handleOpenPreferences} data-testid="reset-cookie-consent-button">
              Cookievoorkeuren opnieuw instellen
            </button>

            <h2 style={s.h2}>4. Cookies in uw browser uitschakelen</h2>
            <p>U kunt cookies ook uitschakelen via de instellingen van uw browser:</p>
            <ul style={s.list}>
              <li><a style={s.link} href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
              <li><a style={s.link} href="https://support.mozilla.org/nl/kb/cookies-verwijderen-gegevens-wissen-websites-opgeslagen" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
              <li><a style={s.link} href="https://support.apple.com/nl-nl/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
              <li><a style={s.link} href="https://support.microsoft.com/nl-nl/microsoft-edge/cookies-verwijderen-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
            </ul>

            <h2 style={s.h2}>5. Vragen?</h2>
            <p>
              Mail ons op <a style={s.link} href={`mailto:${company.email}`}>{company.email}</a>. Zie ook ons{' '}
              <Link style={s.link} to="/privacy">privacybeleid</Link>.
            </p>
          </article>
        ) : (
          <article style={s.article}>
            <p>
              {company.brand} uses cookies and similar technologies to make the website work properly, analyse usage and
              improve your experience. In this cookies policy we explain which cookies we use and how to manage them.
            </p>

            <h2 style={s.h2}>1. What are cookies?</h2>
            <p>
              Cookies are small text files placed on your device when you visit our website. They help us remember
              preferences and measure usage.
            </p>

            <h2 style={s.h2}>2. Which cookies do we use?</h2>

            <h3 style={s.h3}>2.1 Functional cookies (always active)</h3>
            <p>These cookies are necessary for the website to work. You cannot disable them.</p>
            <ul style={s.list}>
              <li><strong>casaothers_lang</strong> — remembers your language preference (NL/EN). Retention: 1 year.</li>
              <li><strong>casaothers_admin_token</strong> — admin login (administrators only). Retention: 7 days.</li>
              <li><strong>casaothers_cookie_consent</strong> — stores your cookie preferences. Retention: 1 year.</li>
            </ul>

            <h3 style={s.h3}>2.2 Analytics cookies (consent required)</h3>
            <p>
              We use <strong>Google Analytics 4</strong> and <strong>Google Tag Manager</strong> to anonymously understand
              how visitors use our site. This helps us improve user experience and optimise our offerings.
            </p>
            <ul style={s.list}>
              <li><strong>_ga</strong>, <strong>_ga_*</strong> — Google Analytics 4. Retention: 13 months.</li>
              <li><strong>_gid</strong> — distinguishes unique visitors. Retention: 24 hours.</li>
              <li><strong>_gat</strong> — limits request rate. Retention: 1 minute.</li>
            </ul>
            <p>We have a data processing agreement with Google and have enabled IP anonymisation where possible.</p>

            <h2 style={s.h2}>3. Manage your preferences</h2>
            <p>
              You can update your cookie preferences at any time via the button below, or by deleting cookies in your
              browser settings.
            </p>
            <button style={s.button} onClick={handleOpenPreferences} data-testid="reset-cookie-consent-button">
              Reset cookie preferences
            </button>

            <h2 style={s.h2}>4. Disable cookies in your browser</h2>
            <p>You can also disable cookies via your browser settings:</p>
            <ul style={s.list}>
              <li><a style={s.link} href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Google Chrome</a></li>
              <li><a style={s.link} href="https://support.mozilla.org/en-US/kb/clear-cookies-and-site-data-firefox" target="_blank" rel="noopener noreferrer">Mozilla Firefox</a></li>
              <li><a style={s.link} href="https://support.apple.com/en-us/guide/safari/sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
              <li><a style={s.link} href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Microsoft Edge</a></li>
            </ul>

            <h2 style={s.h2}>5. Questions?</h2>
            <p>
              Email us at <a style={s.link} href={`mailto:${company.email}`}>{company.email}</a>. See also our{' '}
              <Link style={s.link} to="/privacy">privacy policy</Link>.
            </p>
          </article>
        )}
      </div>
    </div>
  );
};

export default CookiesPolicy;
